import React from 'react';

import {
  Checkbox,
  filledInputClasses,
  FormControl,
  formControlClasses,
  FormControlLabel,
  FormHelperText,
  formHelperTextClasses,
  Grid2 as Grid,
  inputBaseClasses,
  inputLabelClasses,
  Radio,
  RadioGroup,
  styled,
  TextField,
  textFieldClasses,
  Typography,
} from '@mui/material';

import {
  AlreadyCustomer,
  cleanroomServiceSegments,
  CompanySize,
  CRM_RequestType,
  LeadIntent,
  ServiceSegmentType,
} from '@mewa/types';
import { Dropdown, extractTextMarking, InfoButton, Tooltip } from '@mewa/ui';

import t from '../../../../libs/translate';
import {
  CheckboxWrapper,
  ConversionStepWrapper,
  FormProps,
  getHelperText,
  InputLabel,
} from '../base';
import { FormDataState } from '../hooks/use-form-data';
import {
  ContentToExpand,
  Expander,
  PhoneResponsePreference,
} from './phone-response-preferences';

export type FormDataStepProps = FormProps & {
  serviceSegments: [ServiceSegmentType, string][];
  state: FormDataState;
  formType: CRM_RequestType;
  phoneNumber: string;
  privacyPolicyLink: string;
  formTitle: string;
  maxEmployeeCountForSmallCompany: string;
  featureDev?: boolean;
};

export const FormDataStep = ({
  activeStep,
  state,
  serviceSegments,
  translations,
  formType,
  phoneNumber,
  privacyPolicyLink,
  formTitle,
  maxEmployeeCountForSmallCompany,
  market,
  featureDev,
}: FormDataStepProps) => {
  const privacyLinkData = extractTextMarking(
    t(translations, 'general_form_privacy_label'),
  );

  return (
    <ConversionStepWrapper isActive={activeStep === 0}>
      <FormWrapper>
        <Headline>{formTitle}</Headline>
        <IntroSection
          formType={formType}
          serviceSegments={serviceSegments}
          state={state}
          translations={translations}
          featureDev={featureDev}
        />

        <StepHeader>
          {t(translations, 'general_form_my_contact_information_title')}
        </StepHeader>
        <Grid container>
          <NameGrid size={{ xs: 12 }}>
            <TextField
              label={t(translations, 'general_form_your_name_first_name')}
              fullWidth
              value={state.firstName.value ?? ''}
              onChange={({ target }) =>
                state.firstName.changeValue(target.value)
              }
              onBlur={state.firstName.triggerValidation}
              error={!state.firstName.isValid}
              inputProps={{ maxLength: '40' }}
              helperText={getHelperText(
                !state.firstName.isValid,
                t(translations, 'general_form_first_name_error'),
              )}
            />
            <TextField
              label={t(translations, 'general_form_your_name_last_name')}
              fullWidth
              value={state.lastName.value ?? ''}
              onChange={({ target }) =>
                state.lastName.changeValue(target.value)
              }
              onBlur={state.lastName.triggerValidation}
              error={!state.lastName.isValid}
              inputProps={{ maxLength: '40' }}
              helperText={getHelperText(
                !state.lastName.isValid,
                t(translations, 'general_form_last_name_error'),
              )}
            />
          </NameGrid>
        </Grid>
        <TextField
          label={t(translations, 'general_form_email_label')}
          fullWidth
          value={state.email.value ?? ''}
          onChange={({ target }) => state.email.changeValue(target.value)}
          error={!state.email.isValid}
          inputProps={{ maxLength: '255' }}
          onBlur={state.email.triggerValidation}
          helperText={getHelperText(
            !state.email.isValid,
            t(translations, 'general_form_email_error'),
          )}
        />
        <PhoneFieldWrapper>
          <TextField
            label={phoneNumber}
            sx={{
              width: '64px',
              [`& .${inputBaseClasses.root}`]: {
                borderRightWidth: '0px',

                '&:hover, &:focus': {
                  outline: 'none',
                },
              },
              [`& .${inputBaseClasses.root}.${filledInputClasses.root}`]: {
                backgroundColor: 'transparent',
              },
            }}
            disabled
          />
          <TextField
            label={
              t(translations, 'general_form_phone_label') +
              (state.leadIntent.value !== LeadIntent.recall
                ? ' (' +
                  t(translations, 'general_form_optional_field_suffix') +
                  ')'
                : '')
            }
            fullWidth
            value={state.phone.value ?? ''}
            onChange={({ target }) => state.phone.changeValue(target.value)}
            onBlur={state.phone.triggerValidation}
            inputProps={{ maxLength: '40' }}
            error={!state.phone.isValid}
            helperText={getHelperText(
              !state.phone.isValid,
              t(translations, 'general_form_phone_error'),
            )}
          />
        </PhoneFieldWrapper>
        <PhoneResponsePreference translations={translations} state={state} />
        <TextField
          label={t(translations, 'general_form_company_placeholder')}
          onChange={({ target }) => state.companyName.changeValue(target.value)}
          value={state.companyName.value ?? ''}
          error={!state.companyName.isValid}
          onBlur={state.companyName.triggerValidation}
          fullWidth
          inputProps={{ maxLength: '200' }}
          helperText={getHelperText(
            !state.companyName.isValid,
            t(translations, 'general_form_company_name_error'),
          )}
        />
        <TextField
          label={t(translations, 'general_form_post_code')}
          sx={{
            marginRight: '22px',

            [`& .${inputBaseClasses.root}`]: {
              width: '200px',
            },
          }}
          onChange={({ target }) => state.companyZip.changeValue(target.value)}
          value={state.companyZip.value ?? ''}
          error={!state.companyZip.isValid}
          onBlur={state.companyZip.triggerValidation}
          inputProps={{ maxLength: '10' }}
          helperText={getHelperText(
            !state.companyZip.isValid,
            t(translations, 'general_form_company_zip_error'),
          )}
        />
        <InputLabel>
          {t(translations, 'general_form_company_size_label')}
          <Tooltip
            placement={'top'}
            title={t(translations, 'general_form_company_hint_title')}
            description={t(
              translations,
              'general_form_company_hint_description',
            )}
          >
            <InfoButton />
          </Tooltip>
        </InputLabel>
        <FormControl error={!state.size.isValid}>
          <RadioGroup
            value={state.size.value}
            name="companySize"
            sx={{ marginTop: '-10px', flexDirection: [null, 'row'] }}
            onChange={({ target }) =>
              state.size.changeValue(target.value as CompanySize)
            }
          >
            <FormControlLabel
              value="small"
              control={<Radio />}
              label={t(translations, 'general_form_company_size_small', {
                amount: maxEmployeeCountForSmallCompany ?? '0',
              })}
            />
            <FormControlLabel
              value="large"
              control={<Radio />}
              label={t(translations, 'general_form_company_size_large', {
                amount: maxEmployeeCountForSmallCompany ?? '0',
              })}
            />
          </RadioGroup>
          <FormHelperText>
            {getHelperText(
              !state.size.isValid,
              t(translations, 'general_form_company_size_error'),
            )}
          </FormHelperText>
        </FormControl>

        <FormControl
          error={!state.legal.isValid}
          sx={{
            display: 'grid',
            gridTemplateColumns: 'min-content 1fr',
            gridTemplateRows: '1fr 1fr',
            alignItems: 'start',
            marginLeft: '-11px',
          }}
        >
          <CheckboxWrapper
            sx={{
              display: 'contents',
            }}
            control={
              <Checkbox
                checked={!!state.legal.value}
                onChange={({ target }) =>
                  state.legal.changeValue(target.checked)
                }
              />
            }
            label={
              <Typography
                component="p"
                variant="footnote"
                fontWeight="regular"
                sx={{
                  lineHeight: '1.5',
                  marginTop: '3px',
                  gridColumn: '2 / 2',
                }}
              >
                {privacyLinkData.marking ? privacyLinkData.beforeMarking : ''}
                <InlineLink
                  href={privacyPolicyLink + '/'} // this is not a GatsbyLink so we have to add the trailing slash manually
                  target="_blank"
                  sx={{
                    gridColumn: '2 / 2',
                    gridRows: '1 / 1',
                  }}
                >
                  {privacyLinkData.marking || privacyLinkData.beforeMarking}
                </InlineLink>
                {privacyLinkData.afterMarking}
              </Typography>
            }
          />

          <FormHelperText
            sx={{
              gridColumn: '2 / 2',
              gridRow: '2 / 2',
              marginLeft: '0',
            }}
          >
            {getHelperText(
              !state.legal.isValid,
              t(translations, 'general_form_privacy_error'),
            )}
          </FormHelperText>
        </FormControl>
      </FormWrapper>
    </ConversionStepWrapper>
  );
};

const FormWrapper = styled('div')({
  [`& .${formControlClasses.root}`]: {
    marginBottom: '10px',

    [`.${formHelperTextClasses.root}.${inputLabelClasses.error}`]: {
      height: '20px',
      fontSize: '14px',
      lineHeight: '14px',
    },
  },
});

const IntroSection = ({
  formType,
  translations,
  state,
  serviceSegments,
  featureDev = false,
}: {
  formType: CRM_RequestType;
  translations: FormProps['translations'];
  state: FormDataState;
  serviceSegments: [ServiceSegmentType, string][];
  featureDev?: boolean;
}) => {
  const serviceSegmentOptions = serviceSegments
    .filter(
      ([s]) =>
        featureDev ||
        s === ServiceSegmentType.cleanRoomClothing ||
        !cleanroomServiceSegments.includes(s),
    )
    .map(([key, name]) => ({
      name: name,
      value: key,
    }));
  let selectedServiceSegmentIndex: number | null =
    serviceSegmentOptions.findIndex(
      (s) => s.value === state.serviceSegment.value,
    );
  if (selectedServiceSegmentIndex < 0) {
    selectedServiceSegmentIndex = null;
  }

  const leadIntentOptions = [
    {
      name: t(translations, 'general_form_topic_option_question_about_product'),
      value: LeadIntent.product,
    },
    {
      name: t(translations, 'general_form_topic_option_free_consultation'),
      value: LeadIntent.consultation,
    },
    {
      name: t(translations, 'general_form_topic_option_call_back'),
      value: LeadIntent.recall,
    },
    {
      name: t(translations, 'general_form_topic_option_general_question'),
      value: LeadIntent.question,
    },
  ];

  let selectedLeadIntentIndex: number | null = leadIntentOptions.findIndex(
    (s) => s.value === state.leadIntent.value,
  );
  if (selectedLeadIntentIndex < 0) {
    selectedLeadIntentIndex = null;
  }

  const revalidatePhone = (
    oldLeadIntent: LeadIntent | null,
    newLeadIntent: LeadIntent | null,
  ) => {
    // revalidate the phone number when a user changes the 'lead intent' from 'recall' to another value because the phone number is no longer required
    if (
      oldLeadIntent === LeadIntent.recall &&
      newLeadIntent !== LeadIntent.recall &&
      !state.phone.isValid
    ) {
      // the new value of leadIntent must be passed because the state of this value is updated async
      state.phone.triggerValidation(newLeadIntent);
    }
  };

  return (
    <>
      {formType === CRM_RequestType.OrderService ? (
        <Typography
          component="p"
          variant="footnote"
          sx={{ marginBottom: '60px' }}
        >
          {t(translations, 'brochures_download_form_data_note')}
        </Typography>
      ) : null}

      <>
        {formType === CRM_RequestType.Consultation ? (
          <>
            <InputLabel>
              {t(translations, 'general_form_already_customer_label')}
            </InputLabel>
            <FormControl error={!state.alreadyCustomer.isValid}>
              <RadioGroup
                value={state.alreadyCustomer.value}
                name="companySize"
                sx={{ marginTop: '-10px', flexDirection: 'row' }}
                onChange={({ target }) =>
                  state.alreadyCustomer.changeValue(
                    target.value as AlreadyCustomer,
                  )
                }
              >
                <FormControlLabel
                  value="no"
                  control={<Radio />}
                  label={t(translations, 'general_form_no')}
                />
                <FormControlLabel
                  value="yes"
                  control={<Radio />}
                  label={t(translations, 'general_form_yes')}
                />
              </RadioGroup>
              <FormHelperText>
                {getHelperText(
                  !state.alreadyCustomer.isValid,
                  t(translations, 'general_form_already_customer_error'),
                )}
              </FormHelperText>
            </FormControl>
            <Expander
              className={
                state.alreadyCustomer.value === 'yes' ? 'expanded' : ''
              }
            >
              <ContentToExpand>
                <TextField
                  label={t(translations, 'general_form_customer_number_label')}
                  onChange={({ target }) =>
                    state.customerId.changeValue(target.value)
                  }
                  fullWidth
                  value={state.customerId.value ?? ''}
                  helperText="&nbsp;"
                />
              </ContentToExpand>
            </Expander>

            <div id="lead-intent-wrapper">
              <Dropdown
                idToScroll={'lead-intent-wrapper'}
                id="lead-intent"
                label={t(translations, 'general_form_topic_label')}
                error={!state.leadIntent.isValid}
                items={leadIntentOptions}
                onSelect={(newIdx) => {
                  const oldLeadIntent = state.leadIntent.value;
                  const newLeadIntent = leadIntentOptions[newIdx].value;
                  state.leadIntent.changeValue(newLeadIntent);

                  revalidatePhone(oldLeadIntent, newLeadIntent);
                }}
                onClose={state.leadIntent.triggerValidation}
                activeItem={selectedLeadIntentIndex}
                helperText={getHelperText(
                  !state.leadIntent.isValid,
                  t(translations, 'general_form_topic_error'),
                )}
              />
            </div>
            <Expander
              className={
                state.leadIntent.value === LeadIntent.product ||
                state.leadIntent.value === LeadIntent.consultation
                  ? 'expanded'
                  : ''
              }
            >
              <ContentToExpand>
                <ServiceSegmentsWrapper id="service-segments-wrapper">
                  <Dropdown
                    idToScroll={'service-segments-wrapper'}
                    id="service-segments"
                    label={t(translations, 'general_form_product_label')}
                    error={!state.serviceSegment.isValid}
                    items={serviceSegmentOptions}
                    onSelect={(newIdx) => {
                      state.serviceSegment.changeValue(
                        serviceSegmentOptions[newIdx].value,
                      );
                    }}
                    onClose={state.serviceSegment.triggerValidation}
                    activeItem={selectedServiceSegmentIndex}
                    helperText={getHelperText(
                      !state.serviceSegment.isValid,
                      t(translations, 'general_form_product_error'),
                    )}
                  />
                  <TooltipWrapper>
                    <Tooltip
                      placement={'top'}
                      description={t(translations, 'general_form_product_hint')}
                    >
                      <InfoButton />
                    </Tooltip>
                  </TooltipWrapper>
                </ServiceSegmentsWrapper>
              </ContentToExpand>
            </Expander>
          </>
        ) : null}
        <MessageField state={state} translations={translations} />
      </>
    </>
  );
};

const MessageField = ({
  translations,
  state,
}: {
  translations: FormProps['translations'];
  state: FormDataState;
}) => {
  return (
    <TextField
      label={
        t(translations, 'general_form_my_message_text') +
        ' (' +
        t(translations, 'general_form_optional_field_suffix') +
        ')'
      }
      multiline
      fullWidth
      rows={4}
      sx={{
        [`& .${inputBaseClasses.root}`]: {
          height: '125px',
          paddingTop: '18px',
          paddingLeft: '16px',
        },
        [`& .${inputBaseClasses.root} .${filledInputClasses.input}`]: {
          padding: '0',
          paddingTop: '5px',
        },
      }}
      value={state.message.value ?? ''}
      onChange={({ target }) => state.message.changeValue(target.value)}
      slotProps={{ htmlInput: { maxLength: '500' } }}
    />
  );
};

const Headline = styled('h1')(({ theme }) => ({
  ...theme.typography.h2,
  fontWeight: theme.typography.fontWeightMedium,
  marginBottom: '50px',
  marginTop: '176px',

  [theme.breakpoints.down('md')]: {
    marginTop: '80px',
  },

  [theme.breakpoints.down('sm')]: {
    marginTop: '60px',
    marginBottom: '40px',
  },
}));

export const StepHeader = styled('h2')(({ theme }) => ({
  ...theme.typography.h4,
  marginBottom: '30px',
  marginTop: '50px',
  display: 'flex',
  alignItems: 'center',

  [theme.breakpoints.down('sm')]: {
    marginBottom: '16px',
  },
}));

const InlineLink = styled('a')({
  color: 'var(--color-dark-coal)',
  position: 'relative',
  textDecoration: 'underline',
  padding: '6px 0', // Increase active area
  textUnderlineOffset: '4px',
  textDecorationColor: 'var(--color-dark-coal)',
  transition: 'color var(--transition-duration)',
  '&:hover': {
    color: 'var(--color-light-red)',
    textDecorationColor: 'var(--color-light-red)',
  },
});

const NameGrid = styled(Grid)(({ theme }) => ({
  [`.${textFieldClasses.root}:first-of-type`]: {
    marginBottom: '14px',
  },

  [theme.breakpoints.up('md')]: {
    display: 'flex',

    [`.${textFieldClasses.root}`]: {
      marginLeft: '10px',
    },

    [`.${textFieldClasses.root}:first-of-type`]: {
      marginBottom: 0,
      marginLeft: 0,
      marginRight: '10px',
    },
  },
}));

const PhoneFieldWrapper = styled('div')({
  display: 'flex',
});

const ServiceSegmentsWrapper = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr auto',
}));

const TooltipWrapper = styled('div')(({ theme }) => ({
  maxHeight: '56px',
  display: 'flex',
}));
